import { useState } from 'react';

interface FortuneCookieProps {
  isCracked: boolean;
  onCrack: () => void;
}

export default function FortuneCookie({ isCracked, onCrack }: FortuneCookieProps) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    // Enhanced crack sound effect
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Main crack sound
    const oscillator1 = audioContext.createOscillator();
    const gainNode1 = audioContext.createGain();
    oscillator1.connect(gainNode1);
    gainNode1.connect(audioContext.destination);
    oscillator1.frequency.value = 800;
    oscillator1.type = 'sine';
    gainNode1.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    oscillator1.start(audioContext.currentTime);
    oscillator1.stop(audioContext.currentTime + 0.3);

    // Secondary crack sound (lower pitch)
    const oscillator2 = audioContext.createOscillator();
    const gainNode2 = audioContext.createGain();
    oscillator2.connect(gainNode2);
    gainNode2.connect(audioContext.destination);
    oscillator2.frequency.value = 400;
    oscillator2.type = 'triangle';
    gainNode2.gain.setValueAtTime(0.2, audioContext.currentTime + 0.05);
    gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
    oscillator2.start(audioContext.currentTime + 0.05);
    oscillator2.stop(audioContext.currentTime + 0.25);
    
    onCrack();
  };

  return (
    <div className="flex justify-center items-center mb-8 sm:mb-16">
      <div
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`relative cursor-pointer transition-all duration-500 ${
          isHovered && !isCracked ? 'scale-110' : 'scale-100'
        }`}
        style={{ pointerEvents: 'auto' }}
      >
        <div className="relative" style={{ pointerEvents: 'auto' }}>
          <div className="w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 relative" style={{ pointerEvents: 'auto' }}>
            {/* Outer glow ring */}
            <div className="absolute inset-0 rounded-full neon-glow-ring animate-neonPulse" style={{ pointerEvents: 'none' }}></div>
            
            {/* Main cookie body */}
            <div className="absolute inset-4 sm:inset-8 bg-gradient-to-br from-pink-500/40 via-purple-500/40 to-cyan-500/40 rounded-full transform rotate-45 backdrop-blur-sm border-2 sm:border-4 border-pink-400/60 neon-cookie-glow" style={{ pointerEvents: 'none' }}>
              <div className="absolute inset-2 sm:inset-4 bg-gradient-to-br from-pink-400/30 to-cyan-400/30 rounded-full border border-2 border-cyan-400/40"></div>
              
              {/* Cookie center line */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 sm:w-40 h-0.5 sm:h-1 bg-gradient-to-r from-transparent via-pink-300/60 to-transparent rounded-full"></div>
            </div>
            
            {/* Cookie emoji with glow */}
            <div className="absolute inset-0 flex items-center justify-center" style={{ pointerEvents: 'none' }}>
              <div className="text-5xl sm:text-6xl md:text-8xl filter drop-shadow-[0_0_20px_rgba(255,0,255,0.8)]" style={{ animation: 'cookiePulse 0.8s ease-in-out infinite' }}>
                🥠
              </div>
            </div>

            {/* Hover effect */}
            {isHovered && !isCracked && (
              <>
                <div className="absolute inset-0 bg-pink-400/20 rounded-full animate-ping" style={{ pointerEvents: 'none' }}></div>
                <div className="absolute inset-0 neon-ripple" style={{ pointerEvents: 'none' }}></div>
              </>
            )}
          </div>
          
          {/* Click prompt - only show when not cracked */}
          {!isCracked && (
            <div className="absolute -bottom-8 sm:-bottom-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap" style={{ pointerEvents: 'none' }}>
              <p className="text-cyan-300 font-bold text-sm sm:text-lg tracking-wider neon-text-glow" style={{ animation: 'floatUpDown 2s ease-in-out infinite' }}>
                CLICK ME
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes cookiePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes floatUpDown {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes neonPulse {
          0%, 100% {
            box-shadow: 
              0 0 20px rgba(255, 0, 255, 0.8),
              0 0 40px rgba(255, 0, 255, 0.6),
              0 0 60px rgba(255, 0, 255, 0.4),
              inset 0 0 20px rgba(255, 0, 255, 0.3);
          }
          50% {
            box-shadow: 
              0 0 10px rgba(255, 0, 255, 0.6),
              0 0 20px rgba(255, 0, 255, 0.4),
              0 0 30px rgba(255, 0, 255, 0.2),
              inset 0 0 10px rgba(255, 0, 255, 0.2);
          }
        }

        .neon-glow-ring {
          box-shadow: 
            0 0 30px rgba(255, 0, 255, 0.6),
            0 0 60px rgba(255, 0, 255, 0.4),
            0 0 90px rgba(255, 0, 255, 0.2),
            inset 0 0 30px rgba(255, 0, 255, 0.2);
        }

        .neon-cookie-glow {
          box-shadow: 
            0 0 20px rgba(255, 0, 255, 0.6),
            0 0 40px rgba(0, 255, 255, 0.4),
            inset 0 0 20px rgba(255, 0, 255, 0.3);
        }

        .neon-text-glow {
          text-shadow: 
            0 0 10px #00ffff,
            0 0 20px #00ffff,
            0 0 30px #00ffff;
        }

        .neon-ripple {
          animation: ripple 1s ease-out infinite;
        }

        @keyframes ripple {
          0% {
            box-shadow: 0 0 0 0 rgba(255, 0, 255, 0.7);
          }
          100% {
            box-shadow: 0 0 0 50px rgba(255, 0, 255, 0);
          }
        }

        .animate-neonPulse {
          animation: neonPulse 0.8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
